import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import DefaultDataTable from '../../components/default/DefaultDataTable';
import DefaultCard from '../../components/default/DefaultCard';
import BrandRegistrationFilters from '../../components/BrandRegistrationFilters';
import ButtonTableAction from '../../components/buttons/ButtonTableAction';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import PageHeader from '../../components/PageHeader/PageHeader';
import useBrandRegistrationService from '../../app/services/hooks/useBrandRegistrationService';
import useSimilarityAlertService from '../../app/services/hooks/useSimilarityAlertService';
import { BrandRegistrationApplication, BrandRegistrationFilters as BrandRegistrationFiltersType } from '../../app/models/BrandRegistration';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import { AppContext } from '../../contexts/AppContext';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import usePermissions from '../../hooks/usePermissions';
import '../../styles/page-components.css';
import InputField from '../../components/InputField';
import DefaultModal from '../../components/default/DefaultModal';
import ButtonSaveForm from '../../components/buttons/ButtonSaveForm';
import SelectTwo from '../../components/SelectTwo';
import useOrganizationService from '../../app/services/hooks/useOrganizationService';
import { AuthContext } from '../../contexts/AuthContext';


// Casos cerrados: ya no se reasignan
const CLOSED_STATUSES = ['completed', 'cancelled', 'rejected_inapi', 'feasibility_negative'];

const BrandRegistration = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { can } = usePermissions();
    const history = useHistory();
    const { requestConfirmation, successAlert, errorAlert, infoAlert } = useReactConfirmAlert();
    const [showStagesModal, setShowStagesModal] = useState(false);
    
    // Verificar si el usuario tiene permisos para asignar abogados
    const canAssignLawyers = can('assign_brandregistrationapplication');
    // Las alertas de similitud las revisa el abogado o el estudio (el cliente recibe el aviso por correo)
    const canSeeAlerts = can('view_brandregistrationapplication');
    const { fetchingGetBrandRegistrations, getBrandRegistrations, assignLawyer, deleteBrandRegistration, fetchingDeleteBrandRegistration, getInapiBrandDetail, fetchingInapiBrandDetail } = useBrandRegistrationService();
    const { getAlertsCount } = useSimilarityAlertService();

    // En un estudio, el administrador asigna los casos a sus abogados
    const { auth } = useContext(AuthContext);
    const isFirmAdmin = !!auth?.organization?.is_admin && auth.organization.type === 'firm';
    const { getAssignableLawyers } = useOrganizationService();
    const [assignRow, setAssignRow] = useState<BrandRegistrationApplication | null>(null);
    const [assignableLawyers, setAssignableLawyers] = useState<{ id: number; name: string; email: string }[]>([]);
    const [selectedLawyerId, setSelectedLawyerId] = useState<string>('');
    const [assigning, setAssigning] = useState(false);

    // Modal INAPI
    const [showInapiModal, setShowInapiModal] = useState(false);
    const [inapiDetail, setInapiDetail] = useState<any>(null);
    const [inapiSearchNumber, setInapiSearchNumber] = useState('');

    const [applications, setApplications] = useState<BrandRegistrationApplication[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [pendingAlertsCount, setPendingAlertsCount] = useState(0);

    const [filter, setFilter] = useState<BrandRegistrationFiltersType>({
        page: 1,
        per_page: 10,
        search: '',
        status: ''
    });

    useEffect(() => {
        reloadTable();
        loadAlertsCount();
    }, [filter.page, filter.per_page, filter.search, filter.status]);

    const loadAlertsCount = () => {
        getAlertsCount(undefined, {
            onSuccess: (response: ServiceResponse) => {
                setPendingAlertsCount(response.data.pending_count || 0);
            }
        });
    };

    const reloadTable = () => {
        getBrandRegistrations(filter, {
            onSuccess: (response: ServiceResponse) => {
                setApplications(response.data.applications || []);
                setTotalRows(response.data.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al cargar las solicitudes');
                if (response.data?.to_dashboard) {
                    history.push('/intranet');
                }
            }
        });
    };

    const handleOpenInapiModal = (solicitudNumber?: string) => {
        setInapiDetail(null);
        setInapiSearchNumber(solicitudNumber || '');
        setShowInapiModal(true);
        if (solicitudNumber) {
            fetchInapiDetail(solicitudNumber);
        }
    };

    const fetchInapiDetail = (solicitud: string) => {
        if (!solicitud.trim()) return;
        setInapiDetail(null);
        getInapiBrandDetail(solicitud.trim(), {
            onSuccess: (response: ServiceResponse) => {
                setInapiDetail(response.data);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'No se encontró información en INAPI');
            }
        });
    };

    const columns = [
        {
            name: 'ID',
            selector: (row: BrandRegistrationApplication) => row.id,
            sortable: true,
            width: '80px'
        },
        {
            name: 'Solicitante',
            selector: (row: BrandRegistrationApplication) => `${row.applicant_first_name} ${row.applicant_paternal_last_name}`,
            sortable: true,
            cell: (row: BrandRegistrationApplication) => (
                <div className="applicant-cell">
                    <div className="applicant-name">
                        {`${row.applicant_first_name} ${row.applicant_paternal_last_name}`}
                    </div>
                    <div className="applicant-email">
                        {row.applicant_email}
                    </div>
                </div>
            )
        },
        {
            name: 'Marca',
            selector: (row: BrandRegistrationApplication) => `${getMarkTypeLabel(row.mark_type)} - ${row.brand_name || 'N/A'}`,
            sortable: true,
            cell: (row: BrandRegistrationApplication) => (
                <div className="brand-cell">
                    <div className="mark-type">
                        {getMarkTypeLabel(row.mark_type)}
                    </div>
                    <div className="brand-name">
                        {row.brand_name || 'N/A'}
                    </div>
                </div>
            )
        },
        {
            name: 'Abogado Asignado',
            selector: (row: BrandRegistrationApplication) => row.assigned_lawyer_name || 'Sin asignar',
            sortable: true,
            cell: (row: BrandRegistrationApplication) => (
                <div className="assigned-cell">
                    {canAssignLawyers ? (
                        row.assigned_lawyer_name ? (
                            <div className="assigned-info">
                                <span className="assigned-name">{row.assigned_lawyer_name}</span>
                                {/* El administrador del estudio puede pasar el caso a otro abogado */}
                                {isFirmAdmin && !CLOSED_STATUSES.includes(row.status as string) && (
                                    <ButtonGeneral
                                        onClick={() => openAssignModal(row)}
                                        title="Reasignar"
                                        variant="outline"
                                        size="small"
                                        icon="fa-exchange-alt"
                                    />
                                )}
                            </div>
                        ) : (
                            <ButtonGeneral
                                onClick={() => (isFirmAdmin ? openAssignModal(row) : handleAssignLawyer(row))}
                                title={isFirmAdmin ? 'Asignar abogado' : 'Atender Solicitud'}
                                variant="primary"
                                size="small"
                                icon="fa-user-plus"
                            />
                        )
                    ) : (
                        <div className="assigned-cell">
                            {row.assigned_lawyer_name ? (
                                <span className="assigned-name">{row.assigned_lawyer_name}</span>
                            ) : (
                                <span className="not-assigned">Sin asignar</span>
                            )}
                        </div>
                    )}
                </div>
            )
        },
        {
            name: 'N° INAPI',
            selector: (row: BrandRegistrationApplication) => row.inapi_application_number || '',
            sortable: true,
            width: '130px',
            cell: (row: BrandRegistrationApplication) => (
                <div className="text-center w-100">
                    {row.inapi_application_number ? (
                        <span
                            onClick={() => handleOpenInapiModal(row.inapi_application_number!)}
                            style={{
                                color: '#0d6efd',
                                cursor: 'pointer',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                fontSize: '0.85rem'
                            }}
                            title="Ver detalle INAPI"
                        >
                            {row.inapi_application_number}
                        </span>
                    ) : (
                        <span style={{ color: '#adb5bd', fontSize: '0.8rem' }}>—</span>
                    )}
                </div>
            )
        },
        {
            name: 'Fecha de Creación',
            selector: (row: BrandRegistrationApplication) => new Date(row.created_at).toLocaleDateString('es-CL'),
            sortable: true,
            width: '120px'
        },
        {
            name: 'Fecha de Cierre',
            selector: (row: BrandRegistrationApplication) => row.closing_date ? new Date(row.closing_date).toLocaleDateString('es-CL') : 'N/A',
            sortable: true,
            width: '120px',
            cell: (row: BrandRegistrationApplication) => (
                <div className="closing-date-cell">
                    {row.closing_date ? new Date(row.closing_date).toLocaleDateString('es-CL') : (
                        <span className="no-closing-date">N/A</span>
                    )}
                </div>
            )
        },
        {
            name: 'Estado',
            selector: (row: BrandRegistrationApplication) => row.status,
            sortable: true,
            width: '150px',
            cell: (row: BrandRegistrationApplication) => (
                <div className="status-cell">
                    <span className={`status-dot ${getStatusClass(row.status)}`}></span>
                    {getStatusLabel(row.status)}
                </div>
            )
        },
        {
            name: 'Acciones',
            selector: (row: BrandRegistrationApplication) => row.id,
            sortable: false,
            cell: (row: BrandRegistrationApplication) => (
                <div className="d-flex align-items-center">
                    <ButtonTableAction
                        callbackFunction={() => history.push(`/intranet/brand-registration/stages/${row.id}`)}
                        title="Ver"
                        classIcon="fa-eye"
                        colorIcon="text-info"
                        sizeIcon="16"
                    />
                    {row.can_delete && (
                        <ButtonTableAction
                            callbackFunction={() => handleDelete(row.id)}
                            title="Eliminar"
                            classIcon="fa-trash"
                            colorIcon="text-danger"
                            sizeIcon="16"
                        />
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ];

    const getStatusClass = (status: string) => {
        switch (status) {
            // Fase 1: Solicitud inicial
            case 'pending':
                return 'pending';
            case 'paid':
                return 'review';
            // Fase 2: Asignación
            case 'assigned':
                return 'review';
            case 'in_progress':
                return 'review';
            // Fase 3: Verificación
            case 'verified':
                return 'review';
            case 'feasibility_negative':
                return 'rejected';
            // Fase 4: Pago completo
            case 'payment_pending':
                return 'pending';
            case 'payment_confirmed':
                return 'review';
            // Fase 5: Preparación documentos
            case 'preparing_documents':
                return 'review';
            // Fase 6: Presentación INAPI
            case 'submitted_inapi':
                return 'review';
            case 'formal_examination':
                return 'review';
            case 'published':
                return 'review';
            case 'opposition_period':
                return 'review';
            case 'substantive_examination':
                return 'review';
            // Fase 7: Resolución final
            case 'approved_inapi':
                return 'approved';
            case 'rejected_inapi':
                return 'rejected';
            case 'completed':
                return 'approved';
            case 'cancelled':
                return 'rejected';
            default:
                return 'pending';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            // Fase 1: Solicitud inicial
            case 'pending':
                return 'Pendiente';
            case 'paid':
                return 'Pago inicial realizado';
            // Fase 2: Asignación
            case 'assigned':
                return 'Asignado a abogado';
            case 'in_progress':
                return 'En análisis';
            // Fase 3: Verificación
            case 'verified':
                return 'Verificación completada';
            case 'feasibility_negative':
                return 'Factibilidad negativa';
            // Fase 4: Pago completo
            case 'payment_pending':
                return 'Esperando pago completo';
            case 'payment_confirmed':
                return 'Pago completo confirmado';
            // Fase 5: Preparación documentos
            case 'preparing_documents':
                return 'Preparando documentos INAPI';
            // Fase 6: Presentación INAPI
            case 'submitted_inapi':
                return 'Presentado en INAPI';
            case 'formal_examination':
                return 'Examen de forma INAPI';
            case 'published':
                return 'Publicado en Diario Oficial';
            case 'opposition_period':
                return 'Período de oposición';
            case 'substantive_examination':
                return 'Examen de fondo INAPI';
            // Fase 7: Resolución final
            case 'approved_inapi':
                return 'Aprobado por INAPI';
            case 'rejected_inapi':
                return 'Rechazado por INAPI';
            case 'completed':
                return 'Completado';
            case 'cancelled':
                return 'Cancelado';
            default:
                return status;
        }
    };

    const getMarkTypeLabel = (markType: string) => {
        switch (markType) {
            case 'mixta':
                return 'Mixta';
            case 'figurativa':
                return 'Figurativa';
            case 'denominativa':
                return 'Denominativa';
            default:
                return markType;
        }
    };

    const getInapiStatusColor = (estado: string) => {
        switch (estado?.toUpperCase()) {
            case 'CONC': return '#198754';
            case 'RECH': return '#dc3545';
            case 'PEND': return '#ffc107';
            case 'TRAM': return '#0d6efd';
            case 'ABAN': return '#6c757d';
            default: return '#6c757d';
        }
    };

    const handleSearch = (value: string) => {
        setFilter({ ...filter, search: value, page: 1 });
    };

    const handleStatusFilter = (value: string) => {
        setFilter({ ...filter, status: value, page: 1 });
    };

    const handleFilter = () => {
        // Recargar la tabla con los filtros actuales
        reloadTable();
    };

    const openAssignModal = (row: BrandRegistrationApplication) => {
        setAssignRow(row);
        setSelectedLawyerId(String(row.assigned_lawyer_id ?? auth?.id ?? ''));
        getAssignableLawyers({
            onSuccess: (response) => setAssignableLawyers(response.data.lawyers),
            onError: (response) => toast.error(response.message)
        });
    };

    const handleAssignToLawyer = () => {
        if (!assignRow || !selectedLawyerId) {
            toast.error('Elige un abogado');
            return;
        }
        setAssigning(true);
        assignLawyer(assignRow.id, {
            onSuccess: (response) => {
                setAssigning(false);
                setAssignRow(null);
                toast.success(response.message || 'Solicitud asignada');
                reloadTable();
            },
            onError: (response) => {
                setAssigning(false);
                toast.error(response.message || 'No se pudo asignar la solicitud');
            },
            onFieldError: (response) => {
                // 422: sin esto el botón quedaba en "Cargando..." para siempre
                setAssigning(false);
                const first = response.data && Object.values(response.data as Record<string, string[]>)[0];
                toast.error((Array.isArray(first) ? first[0] : response.message) || 'No se pudo asignar la solicitud');
            }
        }, Number(selectedLawyerId));
    };

    const handleAssignLawyer = (row: BrandRegistrationApplication) => {
        requestConfirmation({
            title: 'Asignar Solicitud',
            message: `¿Está seguro que desea tomar esta solicitud? Se le asignará automáticamente la solicitud #${row.id}.`,
            buttons: {
                confirmButton: {
                    label: 'Tomar solicitud',
                    onClick: () => {
                        if (showLoading) showLoading();
                        assignLawyer(row.id, {
                            onSuccess: (response) => {
                                if (hideLoading) hideLoading();
                                if (successAlert) {
                                    successAlert({
                                        title: 'Éxito',
                                        message: response.message || 'Solicitud asignada correctamente'
                                    });
                                }
                                reloadTable();
                            },
                            onError: (error) => {
                                if (hideLoading) hideLoading();
                                if (errorAlert) {
                                    errorAlert({
                                        title: 'Error',
                                        message: error.message || 'No se pudo asignar la solicitud'
                                    });
                                }
                            },
                            onFieldError: (response) => {
                                // 422 (por ejemplo, otro abogado la tomó antes): cerrar el loader
                                if (hideLoading) hideLoading();
                                const first = response.data && Object.values(response.data as Record<string, string[]>)[0];
                                if (errorAlert) {
                                    errorAlert({
                                        title: 'Error',
                                        message: (Array.isArray(first) ? first[0] : response.message) || 'No se pudo asignar la solicitud'
                                    });
                                }
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => {
                        // Modal se cierra automáticamente
                    }
                }
            }
        });
    };

    const handleDelete = (id: number) => {
        requestConfirmation({
            title: 'Eliminar Solicitud',
            message: `¿Está seguro que desea eliminar la solicitud #${id}? Esta acción no se puede deshacer.`,
            buttons: {
                confirmButton: {
                    label: 'Eliminar',
                    onClick: () => {
                        if (showLoading) showLoading();
                        deleteBrandRegistration(id, {
                            onSuccess: (response) => {
                                if (hideLoading) hideLoading();
                                if (successAlert) {
                                    successAlert({
                                        title: 'Éxito',
                                        message: response.message || 'Solicitud eliminada correctamente'
                                    });
                                }
                                reloadTable();
                            },
                            onError: (error) => {
                                if (hideLoading) hideLoading();
                                if (errorAlert) {
                                    errorAlert({
                                        title: 'Error',
                                        message: error.message || 'No se pudo eliminar la solicitud'
                                    });
                                }
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => {
                        // Modal se cierra automáticamente
                    }
                }
            }
        });
    };


    return (
        <div className="container-fluid">
            <div className="page-container">
                {/* Header con breadcrumbs */}
                <PageHeader
                    title=""
                    breadcrumbs={[
                        { label: 'Inicio', path: '/intranet' },
                        { label: 'Registro de marca', isCurrent: true }
                    ]}
                />

                {/* Header con título y botón de crear alineados */}
                <DefaultCard>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h1 className="page-title">Registro de marca</h1>
                            <p className="page-description">
                                Realiza el seguimiento de las solicitudes de registro de marca.{" "}
                                <a 
                                    href="#" 
                                    className="description-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowStagesModal(true);
                                    }}
                                >
                                    Conoce más sobre las etapas del registro aquí
                                </a>
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            {canSeeAlerts && (
                                <ButtonGeneral
                                    onClick={() => {
                                        history.push('/intranet/brand-registration/similarity-alerts');
                                    }}
                                    title={`Alertas${pendingAlertsCount > 0 ? ` (${pendingAlertsCount})` : ''}`}
                                    variant={pendingAlertsCount > 0 ? 'danger' : 'outline'}
                                    size="small"
                                    icon="fa-bell"
                                    iconPosition="left"
                                />
                            )}
                            <ButtonGeneral
                                onClick={() => {
                                    window.location.href = '/intranet/brand-registration/new';
                                }}
                                title="Crear nuevo registro"
                                variant="primary"
                                size="small"
                                icon="fa-plus"
                                iconPosition="left"
                            />
                        </div>
                    </div>

                    {/* Filters and Actions */}
                    <BrandRegistrationFilters
                        search={filter.search || ''}
                        status={filter.status || ''}
                        onSearchChange={handleSearch}
                        onStatusChange={handleStatusFilter}
                        onFilter={handleFilter}
                    />

                    {/* Data Table */}
                    <DefaultDataTable
                        columns={columns}
                        data={applications}
                        paginationTotalRows={totalRows}
                        filter={filter}
                        setFilter={setFilter}
                        pagination={true}
                        progressPending={fetchingGetBrandRegistrations}
                    />
                </DefaultCard>

                {/* Modal de Detalle INAPI */}
                {showInapiModal && (
                    <div 
                        className="modal fade show" 
                        style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} 
                        tabIndex={-1}
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                setShowInapiModal(false);
                            }
                        }}
                    >
                        <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header" style={{ backgroundColor: '#1a365d' }}>
                                    <h5 className="modal-title" style={{ color: '#ffffff' }}>
                                        <i className="fas fa-search me-2"></i>
                                        Consulta INAPI
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setShowInapiModal(false)}
                                        aria-label="Cerrar"
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {/* Buscador */}
                                    <div className="row mb-4 align-items-end">
                                        <div className="col">
                                            <InputField
                                                label="Número de solicitud INAPI"
                                                value={inapiSearchNumber}
                                                onChange={(value: string) => setInapiSearchNumber(value)}
                                                placeholder="Ej: 1295455"
                                            />
                                        </div>
                                        <div className="col-auto" style={{ paddingBottom: '22px' }}>
                                            <ButtonGeneral
                                                onClick={() => fetchInapiDetail(inapiSearchNumber)}
                                                title="Buscar"
                                                variant="primary"
                                                size="medium"
                                                icon="fa-search"
                                                loading={fetchingInapiBrandDetail}
                                            />
                                        </div>
                                    </div>

                                    {/* Loading */}
                                    {fetchingInapiBrandDetail && (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Cargando...</span>
                                            </div>
                                            <p className="mt-3 text-muted">Consultando INAPI...</p>
                                        </div>
                                    )}

                                    {/* Resultado */}
                                    {!fetchingInapiBrandDetail && inapiDetail && (
                                        <div>
                                            {/* Información General */}
                                            <div className="card mb-3 border-0" style={{ backgroundColor: '#f8f9fa' }}>
                                                <div className="card-body">
                                                    <h6 className="fw-bold mb-3" style={{ color: '#1a365d' }}>
                                                        <i className="fas fa-info-circle me-2"></i>
                                                        Información General
                                                    </h6>
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <div className="mb-2">
                                                                <small className="text-muted d-block">N° Solicitud</small>
                                                                <strong>{inapiDetail.NumeroSolicitud || '—'}</strong>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-2">
                                                                <small className="text-muted d-block">N° Registro</small>
                                                                <strong>{inapiDetail.NumeroRegistro || '—'}</strong>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-2">
                                                                <small className="text-muted d-block">Denominación</small>
                                                                <strong>{inapiDetail.Denominacion || '—'}</strong>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-2">
                                                                <small className="text-muted d-block">Tipo</small>
                                                                <strong>{inapiDetail.TipoCategoriaDescripcion || inapiDetail.TipoCategoria || '—'}</strong>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-2">
                                                                <small className="text-muted d-block">Estado</small>
                                                                <span 
                                                                    className="badge" 
                                                                    style={{ 
                                                                        backgroundColor: getInapiStatusColor(inapiDetail.Estado),
                                                                        fontSize: '0.85rem',
                                                                        padding: '6px 12px'
                                                                    }}
                                                                >
                                                                    {inapiDetail.EstadoDescripcion || inapiDetail.Estado || '—'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-2">
                                                                <small className="text-muted d-block">Fecha Presentación</small>
                                                                <strong>{inapiDetail.FechaPresentacion || '—'}</strong>
                                                            </div>
                                                        </div>
                                                        {inapiDetail.FechaVencimiento && (
                                                            <div className="col-md-6">
                                                                <div className="mb-2">
                                                                    <small className="text-muted d-block">Fecha Vencimiento</small>
                                                                    <strong>{inapiDetail.FechaVencimiento}</strong>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Titulares */}
                                            {inapiDetail.Titulares && inapiDetail.Titulares.length > 0 && (
                                                <div className="card mb-3 border-0" style={{ backgroundColor: '#f8f9fa' }}>
                                                    <div className="card-body">
                                                        <h6 className="fw-bold mb-3" style={{ color: '#1a365d' }}>
                                                            <i className="fas fa-user me-2"></i>
                                                            Titulares
                                                        </h6>
                                                        {inapiDetail.Titulares.map((titular: any, idx: number) => (
                                                            <div key={idx} className={idx > 0 ? 'mt-2 pt-2 border-top' : ''}>
                                                                <strong>{titular.Nombre || titular.NombreCompleto || '—'}</strong>
                                                                {titular.Nacionalidad && (
                                                                    <span className="text-muted ms-2">({titular.Nacionalidad})</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Clases */}
                                            {inapiDetail.Clases && inapiDetail.Clases.length > 0 && (
                                                <div className="card mb-3 border-0" style={{ backgroundColor: '#f8f9fa' }}>
                                                    <div className="card-body">
                                                        <h6 className="fw-bold mb-3" style={{ color: '#1a365d' }}>
                                                            <i className="fas fa-list-ol me-2"></i>
                                                            Clases de Niza
                                                        </h6>
                                                        <div className="table-responsive">
                                                            <table className="table table-sm table-borderless mb-0">
                                                                <thead>
                                                                    <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                                        <th style={{ width: '80px' }}>Clase</th>
                                                                        <th>Especificación</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {inapiDetail.Clases.map((clase: any, idx: number) => (
                                                                        <tr key={idx}>
                                                                            <td>
                                                                                <span className="badge bg-primary">{clase.NumeroClase || clase.ClaseNiza || clase.Clase || clase.Numero || idx + 1}</span>
                                                                            </td>
                                                                            <td style={{ fontSize: '0.85rem' }}>
                                                                                {clase.Especificacion || clase.CoberturaNiza || clase.Descripcion || clase.Detalle || clase.Productos || (() => {
                                                                                    const excluded = ['NumeroClase', 'ClaseNiza', 'Clase', 'Numero', 'Especificacion', 'CoberturaNiza', 'Descripcion', 'Detalle', 'Productos'];
                                                                                    const otherValues = Object.entries(clase)
                                                                                        .filter(([key]) => !excluded.includes(key))
                                                                                        .map(([, val]) => val)
                                                                                        .filter(val => typeof val === 'string' && val.length > 5);
                                                                                    return otherValues.length > 0 ? String(otherValues[0]) : '—';
                                                                                })()}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Instancias / Historial */}
                                            {inapiDetail.Instancias && inapiDetail.Instancias.length > 0 && (
                                                <div className="card mb-3 border-0" style={{ backgroundColor: '#f8f9fa' }}>
                                                    <div className="card-body">
                                                        <h6 className="fw-bold mb-3" style={{ color: '#1a365d' }}>
                                                            <i className="fas fa-history me-2"></i>
                                                            Historial de Instancias
                                                        </h6>
                                                        <div className="table-responsive">
                                                            <table className="table table-sm table-borderless mb-0">
                                                                <thead>
                                                                    <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                                        <th>Fecha</th>
                                                                        <th>Descripción</th>
                                                                        <th>Observaciones</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {inapiDetail.Instancias.map((inst: any, idx: number) => (
                                                                        <tr key={idx}>
                                                                            <td style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                                                                {inst.Fecha || '—'}
                                                                            </td>
                                                                            <td style={{ fontSize: '0.85rem' }}>
                                                                                {inst.EstadoDescripcion || inst.Instancia || inst.Descripcion || '—'}
                                                                            </td>
                                                                            <td style={{ fontSize: '0.85rem' }}>
                                                                                {inst.Observacion || inst.Estado || '—'}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Sin resultados */}
                                    {!fetchingInapiBrandDetail && !inapiDetail && inapiSearchNumber && (
                                        <div className="text-center py-4">
                                            <i className="fas fa-search fa-2x text-muted mb-3"></i>
                                            <p className="text-muted">Ingrese un número de solicitud y presione &ldquo;Buscar&rdquo; para consultar INAPI.</p>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowInapiModal(false)}
                                    >
                                        Cerrar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Etapas del Registro */}
                {showStagesModal && (
                    <div 
                        className="modal fade show" 
                        style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} 
                        tabIndex={-1}
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                setShowStagesModal(false);
                            }
                        }}
                    >
                        <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Etapas del Registro de Marca
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowStagesModal(false)}
                                        aria-label="Cerrar"
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="stages-timeline">
                                        {/* Etapa 1 */}
                                        <div className="stage-item mb-4">
                                            <div className="d-flex align-items-start">
                                                <div className="stage-number me-3">
                                                    <span className="number">1</span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-2 fw-bold">Solicitud Inicial</h6>
                                                    <p className="text-muted mb-2">
                                                        El cliente crea la solicitud de registro de marca y realiza el pago inicial 
                                                        del análisis de viabilidad.
                                                    </p>
                                                    <div className="badge bg-secondary">Pendiente</div>
                                                    <div className="badge bg-info ms-2">Pago inicial realizado</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Etapa 2 */}
                                        <div className="stage-item mb-4">
                                            <div className="d-flex align-items-start">
                                                <div className="stage-number me-3">
                                                    <span className="number">2</span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-2 fw-bold">Asignación y Análisis</h6>
                                                    <p className="text-muted mb-2">
                                                        Un abogado es asignado a la solicitud y realiza el análisis de viabilidad 
                                                        de la marca, buscando conflictos y antecedentes en INAPI.
                                                    </p>
                                                    <div className="badge bg-primary">Asignado a abogado</div>
                                                    <div className="badge bg-warning ms-2">En análisis</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Etapa 3 */}
                                        <div className="stage-item mb-4">
                                            <div className="d-flex align-items-start">
                                                <div className="stage-number me-3">
                                                    <span className="number">3</span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-2 fw-bold">Verificación</h6>
                                                    <p className="text-muted mb-2">
                                                        El abogado completa la verificación y emite un resultado: 
                                                        <strong> Factibilidad positiva</strong> (puede proceder) o 
                                                        <strong> Factibilidad negativa</strong> (no recomendable).
                                                    </p>
                                                    <div className="badge bg-success">Verificación completada</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Etapa 4 */}
                                        <div className="stage-item mb-4">
                                            <div className="d-flex align-items-start">
                                                <div className="stage-number me-3">
                                                    <span className="number">4</span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-2 fw-bold">Pago Completo</h6>
                                                    <p className="text-muted mb-2">
                                                        Si la verificación es positiva, el cliente debe realizar el pago completo 
                                                        del servicio para proceder con la presentación ante INAPI.
                                                    </p>
                                                    <div className="badge bg-warning">Esperando pago completo</div>
                                                    <div className="badge bg-success ms-2">Pago completo confirmado</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Etapa 5 */}
                                        <div className="stage-item mb-4">
                                            <div className="d-flex align-items-start">
                                                <div className="stage-number me-3">
                                                    <span className="number">5</span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-2 fw-bold">Preparación de Documentos</h6>
                                                    <p className="text-muted mb-2">
                                                        El abogado prepara toda la documentación necesaria para la presentación 
                                                        ante INAPI, incluyendo poderes de representación si es necesario.
                                                    </p>
                                                    <div className="badge bg-info">Preparando documentos INAPI</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Etapa 6 */}
                                        <div className="stage-item mb-4">
                                            <div className="d-flex align-items-start">
                                                <div className="stage-number me-3">
                                                    <span className="number">6</span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-2 fw-bold">Proceso INAPI</h6>
                                                    <p className="text-muted mb-2">
                                                        La solicitud pasa por varias etapas en INAPI:
                                                    </p>
                                                    <ul className="text-muted mb-2">
                                                        <li><strong>Presentación:</strong> La solicitud es presentada oficialmente</li>
                                                        <li><strong>Examen de forma:</strong> INAPI revisa requisitos formales (2-3 meses)</li>
                                                        <li><strong>Publicación:</strong> Se publica en el Diario Oficial</li>
                                                        <li><strong>Período de oposición:</strong> 30 días para que terceros se opongan</li>
                                                        <li><strong>Examen de fondo:</strong> INAPI analiza la registrabilidad de la marca</li>
                                                    </ul>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        <div className="badge bg-primary">Presentado en INAPI</div>
                                                        <div className="badge bg-info">Examen de forma</div>
                                                        <div className="badge bg-warning">Publicado</div>
                                                        <div className="badge bg-secondary">Período de oposición</div>
                                                        <div className="badge bg-dark">Examen de fondo</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Etapa 7 */}
                                        <div className="stage-item mb-4">
                                            <div className="d-flex align-items-start">
                                                <div className="stage-number me-3">
                                                    <span className="number">7</span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-2 fw-bold">Resolución Final</h6>
                                                    <p className="text-muted mb-2">
                                                        INAPI emite su resolución definitiva. Si es aprobada, se emite el certificado 
                                                        de registro de marca. Si es rechazada, se notifica al cliente con las razones.
                                                    </p>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        <div className="badge bg-success">Aprobado por INAPI</div>
                                                        <div className="badge bg-danger">Rechazado por INAPI</div>
                                                        <div className="badge bg-success">Completado</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="alert alert-info mt-4 mb-0">
                                        <i className="fas fa-clock me-2"></i>
                                        <strong>Duración estimada:</strong> El proceso completo puede tardar entre 6 y 12 meses desde la presentación en INAPI.
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowStagesModal(false)}
                                    >
                                        Cerrar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            <DefaultModal
                title={`Asignar solicitud #${assignRow?.id ?? ''}`}
                show={!!assignRow}
                handleClose={() => setAssignRow(null)}
                actionButton={<ButtonSaveForm callbackSave={handleAssignToLawyer} loading={assigning} title="Asignar" />}
            >
                <SelectTwo
                    label="Abogado a cargo"
                    inputValue={selectedLawyerId}
                    options={assignableLawyers.map((lawyer) => ({ value: String(lawyer.id), label: `${lawyer.name} · ${lawyer.email}` }))}
                    onChange={(option: any) => setSelectedLawyerId(option?.value || '')}
                />
                <small className="text-muted d-block mt-2">Solo aparecen los abogados y administradores activos de tu estudio.</small>
            </DefaultModal>
            </div>
        </div>
    );
};

export default BrandRegistration;
